import MessageOutlinedIcon from "@mui/icons-material/MessageOutlined";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import PersonRemoveOutlinedIcon from "@mui/icons-material/PersonRemoveOutlined";
import StarIcon from "@mui/icons-material/Star";
import { Avatar, Box, Chip, Dialog, DialogContent, DialogTitle, IconButton, Tooltip, Typography } from "@mui/material";
import type { DateTime } from "luxon";
import { useMemo } from "react";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { SURFACE } from "../../../theme/tokens";
import { freeLabelColor } from "../../../utils/categoricalColors";
import type { NurseInShift } from "../../../utils/models";
import type { SimulationEntry } from "../hooks/useHiringData";
import { COLORS, HiringShiftType, isDayShift, matchesRoleGroup } from "../types";

// Functional / Categorical: section tint colors that must stay stable across themes.
const RECOMMEND_SECTION_BG = {
	WARN: "#FFF4E5",
	SUCCESS: "#E8F5E9",
} as const;

export type MoveCategory = "usually_picks" | "overtime";

export type RecommendationLineItem =
	| { type: "add"; count: number }
	| {
			type: "move";
			staffName: string;
			fromDayName: string;
			toDayName: string;
			category: MoveCategory;
	  };

/** Unique simulation count for a role on a date by shift type (day vs night). */
const getSimulationCountByShift = (simulationData: SimulationEntry[], role: string, date: DateTime, isDay: boolean): number => {
	const seen = new Set<string>();
	const shiftType = isDay ? HiringShiftType.Day : HiringShiftType.Night;
	for (const s of simulationData.filter((s) => s.role === role && s.date.hasSame(date, "day") && s.shiftType === shiftType)) {
		seen.add(s.nurseName);
	}
	return seen.size;
};

/** Get simulated staff names for a role on a date by shift type */
const getSimulatedStaffNames = (simulationData: SimulationEntry[], role: string, date: DateTime, isDay: boolean): Set<string> => {
	const names = new Set<string>();
	const shiftType = isDay ? HiringShiftType.Day : HiringShiftType.Night;
	for (const s of simulationData.filter((s) => s.role === role && s.date.hasSame(date, "day") && s.shiftType === shiftType)) {
		names.add(s.nurseName);
	}
	return names;
};

/** Get initials from name */
const getInitials = (name: string): string => {
	const parts = name.split(" ").filter(Boolean);
	if (parts.length === 0) return "?";
	if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
	return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

export type MissingRecommendShiftFilter = "day" | "night" | null;

export interface HiringMissingRecommendDialogProps {
	open: boolean;
	onClose: () => void;
	role: string;
	date: DateTime;
	dates: DateTime[];
	shiftType: MissingRecommendShiftFilter;
	calculateNeeded: (role: string) => { day: number; night: number; total: number };
	getShiftsForDate: (date: DateTime) => NurseInShift[];
	simulationData: SimulationEntry[];
}

interface StaffMember {
	name: string;
	role: string;
	isRecommended: boolean;
	shouldRemove: boolean;
	message?: string;
}

interface ShiftSection {
	title: string;
	missing: number;
	staff: StaffMember[];
	addCount: number;
}

export const HiringMissingRecommendDialog = ({
	open,
	onClose,
	role,
	date,
	dates,
	shiftType,
	calculateNeeded,
	getShiftsForDate,
	simulationData,
}: HiringMissingRecommendDialogProps) => {
	const { showSnackbar } = useSnackbar();

	const sections = useMemo(() => {
		const needed = calculateNeeded(role);
		const fromDayName = date.toFormat("cccc");

		const getGapForDate = (d: DateTime, isDay: boolean) => {
			const shifts = getShiftsForDate(d).filter((s) => matchesRoleGroup(s.role, role));
			const actual = isDay ? shifts.filter((s) => isDayShift(s.shiftType)).length : shifts.filter((s) => !isDayShift(s.shiftType)).length;
			const sim = getSimulationCountByShift(simulationData, role, d, isDay);
			const need = isDay ? needed.day : needed.night;
			return Math.max(0, need - actual - sim);
		};

		const findBestTargetDay = (currentDate: DateTime, isDay: boolean): DateTime | null => {
			let bestDate: DateTime | null = null;
			let bestGap = 0;
			for (const d of dates) {
				if (d.hasSame(currentDate, "day")) continue;
				const gap = getGapForDate(d, isDay);
				if (gap > bestGap) {
					bestGap = gap;
					bestDate = d;
				}
			}
			return bestDate;
		};

		const buildSection = (neededCount: number, actualShifts: NurseInShift[], isDay: boolean): ShiftSection => {
			const simNames = getSimulatedStaffNames(simulationData, role, date, isDay);
			const simCount = simNames.size;
			const total = actualShifts.length + simCount;
			const missing = neededCount - total;
			const surplus = Math.max(0, -missing);
			const addCount = Math.max(0, missing);

			const toDate = findBestTargetDay(date, isDay);
			const toDayName = toDate ? toDate.toFormat("cccc") : "another day";

			// Build staff list - all actual staff
			const staff: StaffMember[] = actualShifts.map((s, idx) => {
				const shouldRemove = surplus > 0 && idx < surplus;
				const category = Math.random() < 0.5 ? "usually_picks" : "overtime";
				const message = shouldRemove
					? category === "usually_picks"
						? `Usually picks shifts on ${toDayName}. Try moving to ${toDayName}.`
						: "Weekly OT."
					: undefined;
				return {
					name: s.name,
					role: s.role,
					isRecommended: false,
					shouldRemove,
					message,
				};
			});

			// Add simulated (recommended) staff
			for (const name of simNames) {
				staff.push({
					name,
					role,
					isRecommended: true,
					shouldRemove: false,
				});
			}

			return {
				title: isDay ? "Day" : "Night",
				missing,
				staff,
				addCount,
			};
		};

		const actualDay = getShiftsForDate(date).filter((s) => matchesRoleGroup(s.role, role) && isDayShift(s.shiftType));
		const actualNight = getShiftsForDate(date).filter((s) => matchesRoleGroup(s.role, role) && !isDayShift(s.shiftType));

		const daySection = buildSection(needed.day, actualDay, true);
		const nightSection = buildSection(needed.night, actualNight, false);

		if (shiftType === "day") return [daySection];
		if (shiftType === "night") return [nightSection];
		return [daySection, nightSection];
	}, [role, date, dates, shiftType, calculateNeeded, getShiftsForDate, simulationData]);

	const dialogTitle = useMemo(() => {
		return `Missing Shifts – ${date.toFormat("ccc, MMM d")}`;
	}, [date]);

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth="md"
			fullWidth
			slotProps={{
				paper: { sx: { borderRadius: 2 } },
			}}
		>
			<DialogTitle sx={{ fontWeight: 700, fontSize: "1.125rem", pb: 1 }}>{dialogTitle}</DialogTitle>
			<DialogContent dividers sx={{ py: 2 }}>
				{/* Staff Sections */}
				{sections.map((section) => (
					<Box key={section.title} sx={{ mb: 3 }}>
						<Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
							<Typography
								component="h3"
								variant="h6"
								sx={{
									fontWeight: 700,
									fontSize: "0.9375rem",
									color: "primary.main",
									textTransform: "uppercase",
									letterSpacing: "0.04em",
								}}
							>
								{section.title} Shift
							</Typography>
							<Chip
								size="small"
								label={section.missing > 0 ? `Need ${section.missing} more` : section.missing < 0 ? `${Math.abs(section.missing)} surplus` : "Fully staffed"}
								sx={{
									bgcolor: section.missing > 0 ? COLORS.error : section.missing < 0 ? COLORS.warning : COLORS.success,
									color: SURFACE,
									fontWeight: 600,
									fontSize: "0.75rem",
								}}
							/>
						</Box>

						{/* Add staff recommendation */}
						{section.addCount > 0 && (
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									gap: 1.25,
									py: 1.25,
									px: 1.5,
									mb: 2,
									borderRadius: 1.5,
									bgcolor: "action.hover",
									border: "1px solid",
									borderColor: "divider",
								}}
							>
								<PersonAddAltOutlinedIcon sx={{ color: "primary.main", fontSize: 20 }} />
								<Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }}>
									Add {section.addCount} more staff
								</Typography>
							</Box>
						)}

						{/* Remove staff recommendation */}
						{section.missing < 0 && (
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									gap: 1.25,
									py: 1.25,
									px: 1.5,
									mb: 2,
									borderRadius: 1.5,
									bgcolor: RECOMMEND_SECTION_BG.WARN,
									border: "1px solid",
									borderColor: COLORS.warning,
								}}
							>
								<PersonRemoveOutlinedIcon sx={{ color: COLORS.warning, fontSize: 20 }} />
								<Typography variant="body2" sx={{ fontWeight: 600, flex: 1, color: COLORS.warning }}>
									Consider moving {Math.abs(section.missing)} staff to other days
								</Typography>
							</Box>
						)}

						{/* Staff Grid */}
						{section.staff.length > 0 ? (
							<Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
								{section.staff.map((member) => (
									<Box
										key={`${member.name}-${member.isRecommended}`}
										sx={{
											display: "flex",
											flexDirection: "column",
											py: 1.25,
											px: 1.5,
											borderRadius: 1.5,
											bgcolor: member.isRecommended ? RECOMMEND_SECTION_BG.SUCCESS : member.shouldRemove ? RECOMMEND_SECTION_BG.WARN : "grey.100",
											border: "1px solid",
											borderColor: member.isRecommended ? COLORS.success : member.shouldRemove ? COLORS.warning : "divider",
											minWidth: 200,
											maxWidth: 280,
										}}
									>
										<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
											<Avatar
												sx={{
													width: 32,
													height: 32,
													fontSize: "0.75rem",
													fontWeight: 600,
													bgcolor: member.isRecommended ? COLORS.success : freeLabelColor(member.name).fill,
													color: member.isRecommended ? undefined : freeLabelColor(member.name).ink,
												}}
											>
												{getInitials(member.name)}
											</Avatar>
											<Box sx={{ flex: 1, minWidth: 0 }}>
												<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
													<Typography
														variant="body2"
														sx={{
															fontWeight: 600,
															overflow: "hidden",
															textOverflow: "ellipsis",
															whiteSpace: "nowrap",
														}}
													>
														{member.name}
													</Typography>
													{member.isRecommended && (
														<Tooltip title="Recommended">
															<StarIcon sx={{ fontSize: 14, color: COLORS.success }} />
														</Tooltip>
													)}
												</Box>
												<Typography variant="caption" sx={{ color: "text.secondary" }}>
													{member.role}
												</Typography>
											</Box>

											{/* Message button - only for non-recommended staff */}
											{!member.isRecommended && (
												<Tooltip title="Send message">
													<IconButton
														size="small"
														aria-label="Send message"
														onClick={() => showSnackbar("Message sent", "info")}
														sx={{
															color: member.shouldRemove ? COLORS.warning : "primary.main",
															flexShrink: 0,
														}}
													>
														<MessageOutlinedIcon fontSize="small" />
													</IconButton>
												</Tooltip>
											)}
										</Box>

										{/* Message/Reason displayed under the name */}
										{member.message && (
											<Typography
												variant="caption"
												sx={{
													mt: 1,
													pt: 1,
													borderTop: "1px dashed",
													borderColor: COLORS.warning,
													color: COLORS.warning,
													fontStyle: "italic",
													lineHeight: 1.4,
												}}
											>
												{member.message}
											</Typography>
										)}
									</Box>
								))}
							</Box>
						) : (
							<Typography
								variant="body2"
								sx={{
									color: "text.secondary",
									fontStyle: "italic",
								}}
							>
								No staff assigned to this shift.
							</Typography>
						)}
					</Box>
				))}
			</DialogContent>
		</Dialog>
	);
};
